import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, Clock, Users, Download, ExternalLink } from 'lucide-react'
import { Card, CardContent } from '@/components/ui'
import { supabase } from '@/lib/supabase'

interface WaitlistEntry {
  id: string
  email: string
  created_at: string
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  referrer: string | null
  landing_path: string | null
}

export function Waitlist() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchEntries = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false })

      if (fetchError) {
        throw new Error(fetchError.message)
      }

      setEntries(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch waitlist')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEntries()
  }, [fetchEntries])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const handleExport = () => {
    const csv = [
      ['Email', 'Signed Up', 'Source', 'Medium', 'Campaign', 'Referrer', 'Landing Page'].join(','),
      ...entries.map(e => [
        e.email,
        e.created_at,
        e.utm_source || '',
        e.utm_medium || '',
        e.utm_campaign || '',
        e.referrer || '',
        e.landing_path || '',
      ].map(v => `"${v.replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `waitlist-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Get source breakdown
  const sourceBreakdown = entries.reduce((acc, e) => {
    const source = e.utm_source || 'Direct'
    acc[source] = (acc[source] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const sortedSources = Object.entries(sourceBreakdown)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-border)] px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Mail className="w-6 h-6 text-[var(--color-kids-primary)]" />
              <h1 className="font-display text-xl font-bold text-[var(--color-kids-text)]">
                Waitlist
              </h1>
            </div>
          </div>
          {entries.length > 0 && (
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--color-kids-primary)] hover:bg-[var(--color-kids-primary)]/10 rounded-xl transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[var(--color-kids-primary)]/10 rounded-lg">
                    <Users className="w-5 h-5 text-[var(--color-kids-primary)]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--color-kids-text)]">{entries.length}</p>
                    <p className="text-sm text-[var(--color-kids-text-muted)]">Total signups</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Clock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[var(--color-kids-text)]">
                      {entries.filter(e => {
                        const weekAgo = new Date()
                        weekAgo.setDate(weekAgo.getDate() - 7)
                        return new Date(e.created_at) > weekAgo
                      }).length}
                    </p>
                    <p className="text-sm text-[var(--color-kids-text-muted)]">Last 7 days</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-[var(--color-kids-text-muted)] uppercase tracking-wider mb-2">
                  Top Sources
                </p>
                <div className="space-y-1">
                  {sortedSources.length > 0 ? (
                    sortedSources.map(([source, count]) => (
                      <div key={source} className="flex justify-between text-sm">
                        <span className="text-[var(--color-kids-text)]">{source}</span>
                        <span className="text-[var(--color-kids-text-muted)]">{count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[var(--color-kids-text-muted)]">No data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Entry List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[var(--color-kids-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[var(--color-kids-text-muted)]">Loading waitlist...</p>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-red-600">{error}</div>
            </CardContent>
          </Card>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 text-[var(--color-kids-text-muted)]">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No signups yet.</p>
            <p className="text-sm mt-2">Share your landing page to start collecting emails!</p>
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--color-kids-border)]">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-kids-text-muted)] uppercase tracking-wider">
                        Email
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-kids-text-muted)] uppercase tracking-wider">
                        Signed Up
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-kids-text-muted)] uppercase tracking-wider">
                        Source
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--color-kids-text-muted)] uppercase tracking-wider">
                        Referrer
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-kids-border)]">
                    {entries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-[var(--color-kids-bg)] transition-colors">
                        <td className="px-4 py-3">
                          <span className="font-medium text-[var(--color-kids-text)]">{entry.email}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-[var(--color-kids-text-muted)]">
                            {formatDate(entry.created_at)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {entry.utm_source ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-[var(--color-kids-primary)]/10 text-[var(--color-kids-primary)] rounded-full">
                              {entry.utm_source}
                              {entry.utm_campaign && (
                                <span className="ml-1 text-[var(--color-kids-text-muted)]">
                                  / {entry.utm_campaign}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-sm text-[var(--color-kids-text-muted)]">Direct</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {entry.referrer ? (
                            <a
                              href={entry.referrer}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-[var(--color-kids-primary)] hover:underline"
                            >
                              {new URL(entry.referrer).hostname}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-sm text-[var(--color-kids-text-muted)]">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
